(() => {
  let cartOpener;

  const initHeaderDrawerFocus = (root = document) => {
    root.querySelectorAll('header-drawer:not([data-zoria-focus-ready])').forEach((component) => {
      const details = component.querySelector(':scope > details');
      const summary = details?.querySelector(':scope > summary');
      const drawer = details?.querySelector(':scope > .menu-drawer');
      if (!details || !summary || !drawer) return;

      component.dataset.zoriaFocusReady = 'true';
      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'summary',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      const isVisible = (element) => {
        if (!(element instanceof HTMLElement) || element.hidden || element.getAttribute('aria-hidden') === 'true') {
          return false;
        }
        const closedDetails = element.closest('details:not([open])');
        if (closedDetails && closedDetails.querySelector(':scope > summary') !== element) return false;
        const style = getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      };

      const getFocusableElements = () => Array.from(drawer.querySelectorAll(focusableSelector)).filter(isVisible);
      const backgroundState = new Map();
      const hideFromDrawer = (element) => {
        if (!(element instanceof HTMLElement) || element.contains(drawer) || backgroundState.has(element)) return;
        backgroundState.set(element, {
          hadInert: element.hasAttribute('inert'),
          ariaHidden: element.getAttribute('aria-hidden')
        });
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
      };
      const lockDrawerBackground = () => {
        let current = drawer;
        while (current?.parentElement && current.parentElement !== document.documentElement) {
          const parent = current.parentElement;
          Array.from(parent.children).forEach((sibling) => {
            if (sibling !== current) hideFromDrawer(sibling);
          });
          if (parent === document.body) break;
          current = parent;
        }
      };
      const restoreDrawerBackground = () => {
        backgroundState.forEach((state, element) => {
          if (!state.hadInert) element.removeAttribute('inert');
          if (state.ariaHidden === null) {
            element.removeAttribute('aria-hidden');
          } else {
            element.setAttribute('aria-hidden', state.ariaHidden);
          }
        });
        backgroundState.clear();
      };
      const focusDrawer = () => {
        if (!details.open || !component.isConnected) return;
        const closeButton = drawer.querySelector('.menu-drawer__close-menu-btn');
        const target = isVisible(closeButton) ? closeButton : getFocusableElements()[0] || drawer;
        target.focus({ preventScroll: true });
      };
      let focusTimers = [];
      const clearFocusTimers = () => {
        focusTimers.forEach((timer) => window.clearTimeout(timer));
        focusTimers = [];
      };
      const scheduleDrawerFocus = () => {
        clearFocusTimers();
        requestAnimationFrame(() => requestAnimationFrame(focusDrawer));
        [80, 220, 460].forEach((delay) => {
          focusTimers.push(
            window.setTimeout(() => {
              if (details.open && !drawer.contains(document.activeElement)) focusDrawer();
            }, delay)
          );
        });
      };
      const closeDrawer = (event) => {
        if (!details.open) return;
        clearFocusTimers();
        if (typeof component.closeMenuDrawer === 'function') {
          component.closeMenuDrawer(event, summary);
        } else {
          details.removeAttribute('open');
          details.dispatchEvent(new Event('toggle'));
        }
      };

      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        if (details.open) {
          lockDrawerBackground();
          scheduleDrawerFocus();
        } else if (component.isConnected) {
          clearFocusTimers();
          restoreDrawerBackground();
          requestAnimationFrame(() => summary.focus({ preventScroll: true }));
        } else {
          clearFocusTimers();
          restoreDrawerBackground();
        }
      });

      component.zoriaRestoreDrawerBackground = restoreDrawerBackground;
      component.zoriaCloseDrawer = closeDrawer;
      if (details.open) lockDrawerBackground();

      component.addEventListener(
        'keydown',
        (event) => {
          if (!details.open) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            closeDrawer(event);
            return;
          }
          if (event.key !== 'Tab') return;
          const focusableElements = getFocusableElements();
          if (focusableElements.length === 0) {
            event.preventDefault();
            drawer.focus({ preventScroll: true });
            return;
          }

          const first = focusableElements[0];
          const last = focusableElements[focusableElements.length - 1];
          const activeElement = document.activeElement;
          if (!drawer.contains(activeElement)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus({ preventScroll: true });
          } else if (event.shiftKey && activeElement === first) {
            event.preventDefault();
            last.focus({ preventScroll: true });
          } else if (!event.shiftKey && activeElement === last) {
            event.preventDefault();
            first.focus({ preventScroll: true });
          }
        },
        true
      );

      document.addEventListener(
        'focusin',
        (event) => {
          if (!details.open || drawer.contains(event.target) || event.target === summary) return;
          requestAnimationFrame(focusDrawer);
        },
        true
      );
    });
  };

  const initCartFocus = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.zoriaFocusReady === 'true') return;

    drawer.dataset.zoriaFocusReady = 'true';
    const focusDrawer = () => {
      if (!drawer.classList.contains('active') || drawer.contains(document.activeElement)) return;
      if (getComputedStyle(drawer).visibility === 'hidden') {
        requestAnimationFrame(focusDrawer);
        return;
      }
      const dialog = drawer.querySelector('[role="dialog"]');
      const closeButton = Array.from(drawer.querySelectorAll('.drawer__close')).find(
        (button) => getComputedStyle(button).visibility !== 'hidden'
      );
      (closeButton || dialog)?.focus();
    };

    let wasOpen = drawer.classList.contains('active');
    new MutationObserver(() => {
      const isOpen = drawer.classList.contains('active');
      if (!wasOpen && isOpen) requestAnimationFrame(focusDrawer);
      if (wasOpen && !isOpen && cartOpener && cartOpener.isConnected) {
        requestAnimationFrame(() => cartOpener.focus());
      }
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    drawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !drawer.classList.contains('active')) return;
      event.preventDefault();
      drawer.close();
    });
  };

  const initContactSummaries = (root = document) => {
    root.querySelectorAll('contact-form:not([data-zoria-summary-ready])').forEach((component) => {
      const form = component.querySelector('form');
      const summary = component.querySelector('[data-zoria-contact-error-summary]');
      const list = component.querySelector('[data-zoria-contact-error-list]');
      if (!form || !summary || !list) return;

      component.dataset.zoriaSummaryReady = 'true';
      let scheduled = false;
      const render = (focusSummary = false) => {
        const fields = Array.from(form.elements).filter(
          (field) => field instanceof HTMLElement && field.matches(':invalid')
        );

        list.replaceChildren(
          ...fields.map((field) => {
            const label = field.labels && field.labels[0] ? field.labels[0].textContent.trim() : field.name;
            const configuredMessage = field
              .closest('.field-wrapper')
              ?.querySelector('.field-wrapper__error-msg .form__message')
              ?.textContent.trim();
            const message = field.validity.valueMissing && configuredMessage
              ? configuredMessage
              : field.validationMessage;
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = `#${field.id}`;
            link.textContent = `${label}: ${message}`;
            link.addEventListener('click', (event) => {
              event.preventDefault();
              field.focus();
            });
            item.append(link);
            field.setAttribute('aria-invalid', 'true');
            return item;
          })
        );

        form.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
          if (field.validity.valid) field.removeAttribute('aria-invalid');
        });
        summary.hidden = fields.length === 0;
        if (focusSummary && fields.length > 0) summary.focus();
      };

      form.addEventListener(
        'invalid',
        (event) => {
          event.preventDefault();
          if (scheduled) return;
          scheduled = true;
          requestAnimationFrame(() => {
            scheduled = false;
            render(true);
          });
        },
        true
      );
      form.addEventListener('input', () => render());
    });
  };

  const initAccessibilityWidget = () => {
    const root = document.querySelector('[data-zoria-accessibility]');
    if (!root || root.dataset.zoriaAccessibilityReady === 'true') return;

    root.dataset.zoriaAccessibilityReady = 'true';
    const toggle = root.querySelector('[data-a11y-toggle]');
    const panel = root.querySelector('[data-a11y-panel]');
    const close = root.querySelector('[data-a11y-close]');
    const storageKey = 'zoria-accessibility-preferences';
    const classNames = {
      large: 'zoria-a11y-large',
      small: 'zoria-a11y-small',
      contrast: 'zoria-a11y-contrast',
      grayscale: 'zoria-a11y-grayscale',
      underline: 'zoria-a11y-underline',
      focus: 'zoria-a11y-focus',
      motion: 'zoria-a11y-motion'
    };
    const state = {
      size: 'normal',
      contrast: false,
      grayscale: false,
      underline: false,
      focus: false,
      motion: false
    };

    const readState = () => {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
        if (saved.size === 'large' || saved.size === 'small') state.size = saved.size;
        ['contrast', 'grayscale', 'underline', 'focus', 'motion'].forEach((key) => {
          if (typeof saved[key] === 'boolean') state[key] = saved[key];
        });
      } catch (error) {
        return;
      }
    };

    const saveState = () => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        return;
      }
    };

    const applyState = () => {
      document.documentElement.classList.toggle(classNames.large, state.size === 'large');
      document.documentElement.classList.toggle(classNames.small, state.size === 'small');
      ['contrast', 'grayscale', 'underline', 'focus', 'motion'].forEach((key) => {
        document.documentElement.classList.toggle(classNames[key], state[key]);
      });
      root.querySelectorAll('[data-a11y-action]').forEach((button) => {
        const action = button.dataset.a11yAction;
        const active = action === 'large'
          ? state.size === 'large'
          : action === 'small'
            ? state.size === 'small'
            : Boolean(state[action]);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const setOpen = (open, returnFocus = false) => {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        requestAnimationFrame(() => close.focus());
      } else if (returnFocus) {
        requestAnimationFrame(() => toggle.focus());
      }
    };

    readState();
    applyState();
    toggle.addEventListener('click', () => setOpen(panel.hidden));
    close.addEventListener('click', () => setOpen(false, true));
    root.querySelectorAll('[data-a11y-action]').forEach((button) => {
      button.addEventListener('click', () => {
        const action = button.dataset.a11yAction;
        if (action === 'reset') {
          state.size = 'normal';
          state.contrast = false;
          state.grayscale = false;
          state.underline = false;
          state.focus = false;
          state.motion = false;
        } else if (action === 'large' || action === 'small') {
          state.size = state.size === action ? 'normal' : action;
        } else if (Object.prototype.hasOwnProperty.call(state, action)) {
          state[action] = !state[action];
        }
        applyState();
        saveState();
      });
    });
    document.addEventListener('click', (event) => {
      if (!panel.hidden && !root.contains(event.target)) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault();
        setOpen(false, true);
      }
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest('#cart-icon-bubble, button[name="add"], [id^="SectionAtcBtn-"]');
      if (opener) cartOpener = opener;
    },
    true
  );
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const openDrawer = document.querySelector('header-drawer > details[open]')?.closest('header-drawer');
      if (!openDrawer?.zoriaCloseDrawer) return;
      event.preventDefault();
      event.stopPropagation();
      openDrawer.zoriaCloseDrawer(event);
    },
    true
  );
  document.addEventListener('DOMContentLoaded', () => {
    initHeaderDrawerFocus();
    initCartFocus();
    initContactSummaries();
    initAccessibilityWidget();
  });
  document.addEventListener('shopify:section:load', (event) => {
    initHeaderDrawerFocus(event.target);
    initContactSummaries(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('header-drawer').forEach((component) => {
      component.zoriaRestoreDrawerBackground?.();
    });
  });
})();
